import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  MagnifyingGlassIcon, ArrowDownTrayIcon, EyeIcon, XMarkIcon,
  ChevronLeftIcon, ChevronRightIcon, DocumentTextIcon, DocumentArrowDownIcon
} from '@heroicons/react/24/outline'
import api from '../../services/api'

const PAGE_SIZE = 20

interface CamsRecord {
  id: number
  pan_masked: string
  full_name: string | null
  kyc_status: string | null
  fetched_at: string | null
}

interface EsignDoc {
  kyc_id: number
  user_name: string | null
  user_email: string | null
  esign_status: string
  document_id: string | null
  signed_at: string | null
  has_signed_file: boolean
  has_audit_trail: boolean
}

const KYC_STATUS_META: Record<string, string> = {
  verified: 'bg-green-500/10 text-green-400 border-green-500/20',
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
  not_found: 'bg-dark-700 text-gray-400 border-dark-600',
}

const ESIGN_STATUS_META: Record<string, string> = {
  completed: 'bg-green-500/10 text-green-400 border-green-500/20',
  initiated: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  failed: 'bg-red-500/10 text-red-400 border-red-500/20',
}

function Badge({ status, meta }: { status: string | null; meta: Record<string, string> }) {
  if (!status) return <span className="text-gray-500">—</span>
  const cls = meta[status] || 'bg-dark-700 text-gray-400 border-dark-600'
  return <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${cls}`}>{status.replace('_', ' ')}</span>
}

// Fetch a protected file as a blob (axios adds the auth header), then download or open it.
async function fetchBlob(url: string): Promise<string> {
  const res = await api.get(url, { responseType: 'blob' })
  return window.URL.createObjectURL(new Blob([res.data]))
}

async function downloadFile(url: string, filename: string) {
  const objectUrl = await fetchBlob(url)
  const link = document.createElement('a')
  link.href = objectUrl
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
}

async function openFile(url: string) {
  const objectUrl = await fetchBlob(url)
  window.open(objectUrl, '_blank')
}

function Pagination({ page, total, onPage }: { page: number; total: number; onPage: (p: number) => void }) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  if (total === 0) return null
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-dark-700">
      <p className="text-xs text-gray-400">
        Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
      </p>
      <div className="flex items-center gap-2">
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page === 1}
          className="w-8 h-8 rounded-lg border border-dark-600 bg-dark-900 flex items-center justify-center text-gray-300 hover:bg-dark-700 disabled:opacity-30 transition-colors">
          <ChevronLeftIcon style={{ width: 15, height: 15 }} />
        </button>
        <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
        <button onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
          className="w-8 h-8 rounded-lg border border-dark-600 bg-dark-900 flex items-center justify-center text-gray-300 hover:bg-dark-700 disabled:opacity-30 transition-colors">
          <ChevronRightIcon style={{ width: 15, height: 15 }} />
        </button>
      </div>
    </div>
  )
}

// ─── CAMS raw-data preview modal ──────────────────────────────────────────────

function CamsPreviewModal({ recordId, onClose }: { recordId: number; onClose: () => void }) {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get(`/admin/documents/cams/${recordId}`).then(res => setData(res.data)).catch(() => {}).finally(() => setLoading(false))
  }, [recordId])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6 overflow-y-auto">
      <div className="bg-dark-800 rounded-2xl shadow-xl w-full max-w-3xl p-6 border border-dark-700 my-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-bold text-white text-lg">CAMS KYC Record</h3>
            {data && <p className="text-sm text-gray-400 mt-0.5">{data.full_name} · {data.pan_masked}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <XMarkIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading…</div>
        ) : (
          <pre className="bg-dark-900 border border-dark-700 rounded-xl p-4 text-xs text-gray-300 overflow-auto max-h-[60vh] font-mono">
            {JSON.stringify(data?.raw_data ?? {}, null, 2)}
          </pre>
        )}
      </div>
    </div>
  )
}

// ─── CAMS tab ─────────────────────────────────────────────────────────────────

function CamsTab() {
  const [records, setRecords] = useState<CamsRecord[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [preview, setPreview] = useState<number | null>(null)

  const fetch = async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE }
      if (search) params.search = search
      const res = await api.get('/admin/documents/cams', { params })
      setRecords(res.data.records)
      setTotal(res.data.total)
    } catch {}
    finally { setLoading(false) }
  }
  useEffect(() => { fetch() }, [search, page])
  useEffect(() => { setPage(1) }, [search])

  return (
    <>
      <div className="relative max-w-sm mb-4">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ width: 16, height: 16 }} />
        <input type="text" placeholder="Search PAN or name…" value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
      </div>

      <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700 bg-dark-900 text-left text-xs text-gray-400 uppercase tracking-wider">
                <th className="px-4 py-3 font-semibold">PAN</th>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">KYC Status</th>
                <th className="px-4 py-3 font-semibold">Fetched</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400">Loading…</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400">No CAMS records yet</td></tr>
              ) : records.map(r => (
                <tr key={r.id} className="hover:bg-dark-700 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-300">{r.pan_masked}</td>
                  <td className="px-4 py-3 text-gray-100">{r.full_name || '—'}</td>
                  <td className="px-4 py-3"><Badge status={r.kyc_status} meta={KYC_STATUS_META} /></td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                    {r.fetched_at ? new Date(r.fetched_at).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setPreview(r.id)} title="Preview"
                        className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors">
                        <EyeIcon style={{ width: 16, height: 16 }} />
                      </button>
                      <button onClick={() => downloadFile(`/admin/documents/cams/${r.id}/download-pdf`, `CAMS_KYC_${r.pan_masked}.pdf`)} title="Download PDF"
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                        <DocumentArrowDownIcon style={{ width: 16, height: 16 }} />
                      </button>
                      <button onClick={() => downloadFile(`/admin/documents/cams/${r.id}/download`, `cams-${r.pan_masked}.json`)} title="Download JSON"
                        className="p-1.5 text-gray-400 hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors">
                        <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} onPage={setPage} />
      </div>

      {preview !== null && <CamsPreviewModal recordId={preview} onClose={() => setPreview(null)} />}
    </>
  )
}

// ─── eSign tab ────────────────────────────────────────────────────────────────

function EsignTab() {
  const [docs, setDocs] = useState<EsignDoc[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)

  const fetch = async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE }
      if (search) params.search = search
      if (statusFilter) params.status = statusFilter
      const res = await api.get('/admin/documents/esign', { params })
      setDocs(res.data.documents)
      setTotal(res.data.total)
    } catch {}
    finally { setLoading(false) }
  }
  useEffect(() => { fetch() }, [search, statusFilter, page])
  useEffect(() => { setPage(1) }, [search, statusFilter])

  const fileUrl = (kycId: number, kind: 'signed' | 'audit', disposition: 'inline' | 'attachment') =>
    `/admin/documents/esign/${kycId}/file?kind=${kind}&disposition=${disposition}`

  return (
    <>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-48 max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ width: 16, height: 16 }} />
          <input type="text" placeholder="Search user or email…" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-sm bg-dark-900 border border-dark-600 text-white rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500">
          <option value="">All Statuses</option>
          <option value="initiated">Initiated</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700 bg-dark-900 text-left text-xs text-gray-400 uppercase tracking-wider">
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Signed</th>
                <th className="px-4 py-3 font-semibold">Signed Agreement</th>
                <th className="px-4 py-3 font-semibold">Audit Trail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400">Loading…</td></tr>
              ) : docs.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400">No eSign agreements yet</td></tr>
              ) : docs.map(d => (
                <tr key={d.kyc_id} className="hover:bg-dark-700 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-gray-100">{d.user_name || '—'}</p>
                    <p className="text-xs text-gray-500">{d.user_email}</p>
                  </td>
                  <td className="px-4 py-3"><Badge status={d.esign_status} meta={ESIGN_STATUS_META} /></td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                    {d.signed_at ? new Date(d.signed_at).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openFile(fileUrl(d.kyc_id, 'signed', 'inline'))} title="Preview"
                        className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors">
                        <EyeIcon style={{ width: 16, height: 16 }} />
                      </button>
                      <button onClick={() => downloadFile(fileUrl(d.kyc_id, 'signed', 'attachment'), `esign-${d.kyc_id}-signed.pdf`)} title={d.has_signed_file ? "Download Original PDF" : "Download Generated PDF"}
                        className="p-1.5 text-gray-400 hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors">
                        <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
                      </button>
                      {!d.has_signed_file && <span className="ml-1 text-[10px] text-amber-500/70 border border-amber-500/20 px-1 rounded bg-amber-500/10">Generated</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {d.has_audit_trail ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => openFile(fileUrl(d.kyc_id, 'audit', 'inline'))} title="Preview"
                          className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors">
                          <EyeIcon style={{ width: 16, height: 16 }} />
                        </button>
                        <button onClick={() => downloadFile(fileUrl(d.kyc_id, 'audit', 'attachment'), `esign-${d.kyc_id}-audit.pdf`)} title="Download"
                          className="p-1.5 text-gray-400 hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors">
                          <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    ) : <span className="text-xs text-gray-600">Not stored</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} onPage={setPage} />
      </div>
    </>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DocumentsPage() {
  const [tab, setTab] = useState<'cams' | 'esign'>('cams')

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-xs text-gray-400 mb-1"><Link to="/admin" className="hover:text-amber-400 transition-colors">RA Desk</Link> &rsaquo; Documents</nav>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <DocumentTextIcon style={{ width: 22, height: 22 }} className="text-gray-400" />
          Documents
        </h1>
        <p className="text-sm text-gray-400 mt-0.5">CAMS KYC records and signed eSign agreements — preview, download &amp; search</p>
      </div>

      <div className="flex gap-1 bg-dark-800 border border-dark-700 rounded-xl p-1 w-fit">
        {([['cams', 'CAMS KYC Records'], ['esign', 'eSign Agreements']] as [typeof tab, string][]).map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === key ? 'bg-dark-700 text-white' : 'text-gray-400 hover:text-gray-200'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'cams' ? <CamsTab /> : <EsignTab />}
    </div>
  )
}
